/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// ERROR HANDLING HOOK
// ============================================================================
// Provides standardized error handling patterns for list pages

"use client";

import { logger } from "@/lib/utils/logger";
import { useEffect } from "react";
import { toast } from "sonner";

export interface UseErrorHandlingOptions {
  /**
   * Error object from API/data fetching hook
   * Can be string, Error, or any error-like object
   */
  error: unknown;
  /**
   * Entity label for error messages (e.g., "projects", "clients")
   */
  entityLabel: string;
  /**
   * Entity label plural for error messages (e.g., "Projects", "Clients")
   */
  entityLabelPlural?: string;
  /**
   * Custom error title (overrides default)
   */
  title?: string;
  /**
   * Custom error description (overrides default)
   */
  description?: string;
  /**
   * Whether to log the error (default: true)
   */
  logError?: boolean;
  /**
   * Custom log context (additional data to include in log)
   */
  logContext?: Record<string, unknown>;
  /**
   * Whether error handling is enabled (default: true)
   */
  enabled?: boolean;
}

/**
 * Standardized error handling for list pages
 *
 * Handles:
 * - Error logging with context
 * - Toast notification display
 * - Default error messages
 *
 * @example
 * ```tsx
 * const { error } = useProjects({ ... });
 *
 * useErrorHandling({
 *   error,
 *   entityLabel: "projects",
 *   entityLabelPlural: "Projects",
 * });
 * ```
 */
export function useErrorHandling(options: UseErrorHandlingOptions): void {
  const {
    error,
    entityLabel,
    entityLabelPlural = entityLabel.charAt(0).toUpperCase() +
      entityLabel.slice(1),
    title,
    description,
    logError = true,
    logContext,
    enabled = true,
  } = options;

  useEffect(() => {
    if (!enabled || !error) return;

    // Log error
    if (logError) {
      logger.error(`Failed to load ${entityLabel}`, {
        error,
        entityLabel,
        ...logContext,
      });
    }

    // Show toast notification
    const errorTitle = title ?? `Unable to load ${entityLabelPlural}`;
    const errorDescription =
      description ??
      (typeof error === "string"
        ? error
        : error instanceof Error
          ? error.message
          : `Failed to load ${entityLabelPlural}`);

    toast.error(errorTitle, { description: errorDescription });
  }, [
    error,
    entityLabel,
    entityLabelPlural,
    title,
    description,
    logError,
    logContext,
    enabled,
  ]);
}
